import type { DocumentLink, ExtraMetadata } from "@/models/document-link/DocumentLink";
import {
  buildWorkspaceReferenceVisibilityContext,
  collectDocumentWorkspaceReferenceLinks,
  filterWorkspaceReferenceLinksForDisplay,
  otherSideIsActiveRevision,
  otherSideWorkspaceCollapseKey,
  otherSideWorkspaceRevision,
  type WorkspaceReferenceVisibilityContext,
} from "@/utils/documentLinkWorkspaceVersionCollapse";
import { resolveLegislationDocumentTypeIdFromLabel } from "@/utils/resolveLegislationDocumentTypeIdFromLabel";

export function parseLinkExtraMetadata(
  extraMetadata?: Record<string, string> | string
): ExtraMetadata {
  if (!extraMetadata) {
    return {};
  }
  if (typeof extraMetadata === "object") {
    return extraMetadata as ExtraMetadata;
  }
  if (typeof extraMetadata === "string") {
    try {
      return JSON.parse(extraMetadata) as ExtraMetadata;
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * True when the open document is the link origin (outgoing reference).
 * When current document id is unknown, treat as outgoing so navigation matches legacy destination-based behavior.
 */
export function isOutgoingReferenceFromCurrentDoc(
  link: DocumentLink,
  currentDocumentId: string | undefined
): boolean {
  if (!currentDocumentId) {
    return true;
  }
  if (link.originDocumentId === currentDocumentId) {
    return true;
  }
  if (link.destinationDocumentId === currentDocumentId) {
    return false;
  }
  return true;
}

function coerceHistoricalFlag(value: unknown): boolean {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === "string") return value === "true";
  return false;
}

/**
 * True when the other end of the reference is a workspace document (not legislation).
 */
export function isOtherSideWorkspaceLink(
  link: DocumentLink,
  currentDocumentId: string | undefined
): boolean {
  const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, currentDocumentId);
  return isOutgoing
    ? Boolean(link.destinationWorkspaceId)
    : Boolean(link.originWorkspaceId);
}

/**
 * Workspace display name on the other end of the reference, from API enrichment
 * (`originWorkspaceName` / `destinationWorkspaceName` on {@link DocumentLink}).
 */
export function getOtherSideWorkspaceName(
  link: DocumentLink,
  currentDocumentId: string | undefined
): string | undefined {
  const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, currentDocumentId);
  const raw = isOutgoing
    ? link.destinationWorkspaceName
    : link.originWorkspaceName;
  const t = raw?.trim();
  return t || undefined;
}

/**
 * Full legislation title for tooltips when the other end is not a workspace document
 * (prefers official title, then popular title from link metadata).
 */
export function getOtherSideLegislationTitleForTooltip(
  link: DocumentLink,
  currentDocumentId: string | undefined
): string | undefined {
  if (!currentDocumentId || isOtherSideWorkspaceLink(link, currentDocumentId)) {
    return undefined;
  }
  const metadata = parseLinkExtraMetadata(link.extraMetadata);
  const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, currentDocumentId);
  const official = (
    isOutgoing ? metadata.destination_title : metadata.origin_title
  )?.trim();
  const popular = (
    isOutgoing ? metadata.destination_popular_title : metadata.origin_popular_title
  )?.trim();
  return official || popular || undefined;
}

/**
 * Retsinformation document type id for the document on the other end: from link
 * enrichment (`*_document_type_id`, added by the API), falling back to the
 * "VEJ nr …" style short-name prefix when a link predates that enrichment.
 */
export function getOtherSideLegislationTypeId(
  link: DocumentLink,
  isOutgoing: boolean
): string | undefined {
  const metadata = parseLinkExtraMetadata(link.extraMetadata);
  const enriched = (
    isOutgoing
      ? metadata.destination_document_type_id
      : metadata.origin_document_type_id
  )?.trim();
  if (enriched) return enriched;

  const label = isOutgoing
    ? link.destinationDocumentName?.trim() ||
      metadata.destination_short_name?.trim()
    : link.originDocumentName?.trim() || metadata.origin_short_name?.trim();
  return resolveLegislationDocumentTypeIdFromLabel(label);
}

/**
 * True when the other end is superseded/revoked legislation for “Vis historiske”
 * (`*_is_historical` in link metadata). Workspace revision state is handled separately.
 */
export function isOtherSideHistorical(
  link: DocumentLink,
  currentDocumentId: string | undefined
): boolean {
  const metadata = parseLinkExtraMetadata(link.extraMetadata);
  const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, currentDocumentId);
  return coerceHistoricalFlag(
    isOutgoing
      ? metadata.destination_is_historical
      : metadata.origin_is_historical
  );
}

/**
 * True when the other end is a workspace document on a non-active revision
 * (“Vis tidligere versioner”).
 */
export function isOtherSideEarlierWorkspaceVersion(
  link: DocumentLink,
  currentDocumentId: string | undefined,
  visibilityContext?: WorkspaceReferenceVisibilityContext
): boolean {
  if (!currentDocumentId || !isOtherSideWorkspaceLink(link, currentDocumentId)) {
    return false;
  }

  if (otherSideIsActiveRevision(link, currentDocumentId) === false) {
    return true;
  }
  if (!visibilityContext) {
    return false;
  }

  const key = otherSideWorkspaceCollapseKey(link, currentDocumentId);
  const version = otherSideWorkspaceRevision(link, currentDocumentId);
  const maxVersion = visibilityContext.maxOtherSideRevisionByKey.get(key);
  return (
    version !== undefined &&
    maxVersion !== undefined &&
    version < maxVersion
  );
}

/** True when an injected/native anchor should render as a link (not plain text). */
export function shouldShowDocumentLinkAnchor(
  links: DocumentLink[],
  currentDocumentId: string | undefined,
  showHistorical: boolean,
  showEarlierWorkspaceVersions = false,
  allDocumentLinksForVisibility?: DocumentLink[],
  workspaceVisibilityContext?: WorkspaceReferenceVisibilityContext
): boolean {
  if (!currentDocumentId) return true;

  const pool = showHistorical
    ? links
    : links.filter((l) => !isOtherSideHistorical(l, currentDocumentId));
  if (pool.length === 0) return false;

  if (showEarlierWorkspaceVersions) {
    return true;
  }

  if (!pool.some((l) => isOtherSideWorkspaceLink(l, currentDocumentId))) {
    return true;
  }

  const context =
    workspaceVisibilityContext ??
    buildWorkspaceReferenceVisibilityContext(
      collectDocumentWorkspaceReferenceLinks(
        allDocumentLinksForVisibility ?? links,
        currentDocumentId
      ),
      currentDocumentId
    );

  const visible = filterWorkspaceReferenceLinksForDisplay(
    pool,
    currentDocumentId,
    showEarlierWorkspaceVersions,
    context
  );

  return visible.length > 0;
}

/**
 * Human-readable label for the document on the other end of the reference
 * (aligned with the references sidebar).
 */
export function getOtherSideReferenceLabel(
  link: DocumentLink,
  currentDocumentId: string | undefined
): string {
  const metadata = parseLinkExtraMetadata(link.extraMetadata);
  const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, currentDocumentId);

  const serverName = (
    isOutgoing ? link.destinationDocumentName : link.originDocumentName
  )?.trim();
  const shortName = (
    isOutgoing ? metadata.destination_short_name : metadata.origin_short_name
  )?.trim();
  const title = (
    isOutgoing ? metadata.destination_title : metadata.origin_title
  )?.trim();
  const popularTitle = (
    isOutgoing ? metadata.destination_popular_title : metadata.origin_popular_title
  )?.trim();
  const name = link.name?.trim();

  const display =
    serverName ||
    shortName ||
    name ||
    popularTitle ||
    title ||
    "Unavngivet";

  const version = isOutgoing
    ? link.destinationVersion
    : link.originVersion;
  return version != null ? `${display} v${version}` : display;
}
